import React, { useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import "./NotificationBanner.css";
import SuccessIcon from "../../../assets/icons/notification-success.svg";
import CloseIcon from "../../../assets/icons/notification-close-btn.svg";
import ErrorIcon from "../../../assets/icons/notification-failure.svg";
import WarningIcon from "../../../assets/icons/notification-warning.svg";

/**
 * NotificationBanner
 * A reusable, accessible notification banner.
 *
 * @param {string}   type        - "success" | "error" | "warning" | "info"
 * @param {string}   title       - Bold heading text
 * @param {string}   message     - Supporting message below the title
 * @param {function} onClose     - Callback when close button is clicked; omit to hide the button
 * @param {number}   autoDismiss - Auto-dismiss after N milliseconds; 0 = no auto-dismiss
 */
const NotificationBanner = React.memo(({ type, title, message, onClose, autoDismiss }) => {
  const handleClose = useCallback(() => {
    onClose?.();
  }, [onClose]);

  useEffect(() => {
    if (!autoDismiss || autoDismiss <= 0) return;
    const timer = setTimeout(() => {
      onClose?.();
    }, autoDismiss);
    return () => clearTimeout(timer);
  }, [autoDismiss, onClose]);

  const defaultIcons = { success: SuccessIcon, error: ErrorIcon, warning: WarningIcon, exception: SuccessIcon };

  return (
    <div role="alert" aria-live="polite" className={`notification-banner notification-banner--${type}`}>
      <div className="notification-banner__left">
        <span className="notification-banner__icon">
          <img src={defaultIcons[type]} alt="" aria-hidden="true" />
        </span>
        <div className="notification-banner__content">
          {title && <span className="notification-banner__title">{title}</span>}
          {message && <span className="notification-banner__message">{message}</span>}
        </div>
      </div>
      <div>
        {onClose && (
          <button className="notification-banner__close" onClick={handleClose} aria-label="Dismiss notification" type="button">
            <img src={CloseIcon} alt="" aria-hidden="true" />
          </button>
        )}
      </div>
    </div>
  );
});

NotificationBanner.displayName = "NotificationBanner";

NotificationBanner.propTypes = {
  /** Visual style / severity */
  type: PropTypes.oneOf(["success", "error", "warning", "info", "exception"]),
  /** Bold title text */
  title: PropTypes.string,
  /** Supporting message */
  message: PropTypes.string,
  /** Called when the close button is pressed or autoDismiss fires */
  onClose: PropTypes.func,
  /** Auto-dismiss delay in ms. 0 or undefined = never */
  autoDismiss: PropTypes.number,
};

NotificationBanner.defaultProps = {
  type: "success",
  title: "",
  message: "",
  onClose: undefined,
  autoDismiss: 0,
};

export default NotificationBanner;
